import CryptoJS from 'crypto-js';
import { keysConstants } from '_constants';
import Axios from  'axios-observable';

export const utilitiesService = {
  encrypt,
  decrypt,
  scrape,

    getSessionUser
};

/**
 * Encrypts the given text using AES encryption.
 *
 * @param {string} text - The text to be encrypted.
 * @returns {string} The encrypted text.
 */
function encrypt(text){
  return CryptoJS.AES.encrypt(text,
    CryptoJS.enc.Utf8.parse(keysConstants.SECRET_KEY),
    {
      iv: CryptoJS.enc.Utf8.parse(keysConstants.PARAMETER_SPEC),
      mode: CryptoJS.mode.CBC,
      padding : CryptoJS.pad.Pkcs7
    }

  ).toString();
}

/**
 * Decrypts the given text using AES encryption algorithm.
 *
 * @param {string} text - The encrypted text to be decrypted.
 * @returns {string} - The decrypted text. Returns an empty string if the input text is null.
 */
function decrypt(text){
  try {
    if(text === null) {
        return '';
      }
      
      return CryptoJS.AES.decrypt(text,
        CryptoJS.enc.Utf8.parse(keysConstants.SECRET_KEY),
        {
          iv: CryptoJS.enc.Utf8.parse(keysConstants.PARAMETER_SPEC),
          mode: CryptoJS.mode.CBC,
          padding : CryptoJS.pad.Pkcs7
        }
      ).toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Problem decrypting text:",  text);
    return ""
  }
}


/**
 * Scrapes the given URL and checks if the provided code is present in the HTML response.
 *
 * @param {string} url - The URL to scrape.
 * @param {string} code - The code to search for in the HTML response.
 * @returns {boolean} - Returns true if the code is found in the HTML response, otherwise false.
 */
function scrape(url, code){
  Axios.get(url)
    .subscribe(
      (response) => {
        let isMatched = false;
        if(response.status === 200) {
          let html = response.data;
          let codeUpdated = code;
          html = html.replace(/(\r\n|\n|\r)/gm,"").replace(" ", "");
          isMatched = html.includes(codeUpdated);
          localStorage.setItem('verifyEmbedCode', isMatched);
        }
        return isMatched;
      },
      (error) => console.log(error) );
}

/**
 * Retrieves and decrypts lead identifiers based on the specified identifier type and lead data.
 *
 * @param {string} identifier - The identifier to match against lead data.
 * @param {string} identifierType - The type of identifier ('Form Group' or 'Group Field').
 * @param {Array} leadData - An array of lead data objects containing group_id, field_id, and value properties.
 * @returns {string} - A string of decrypted identifier values, concatenated and with extra spaces removed.
 */
export function getLeadIdentifier(identifier, identifierType, leadData) {
    let identifierVal = [];
    leadData.forEach((val) => {
        if (identifierType === 'Form Group') {
            if (val.group_id === identifier) {
                identifierVal.push(decrypt(val.value));
            }
        } else if (identifierType === 'Group Field') {
            if (val.field_id === identifier) {
                identifierVal.push(decrypt(val.value));
            }
        }
    });

    return identifierVal.join(" ").replace(/\s\s+/g, ' ');
}

/**
 * Retrieves the lead sub-identifier based on the given identifier and identifier type.
 *
 * @param {number|string} identifier - The identifier to match against.
 * @param {string} identifierType - The type of identifier ('Form Group' or 'Group Field').
 * @param {Array} leadData - The array of lead data objects to search through.
 * @returns {Array} An array of objects containing the decrypted value and verify type.
 */
export function getLeadSubIdentifier(identifier, identifierType, leadData) {
    let identifierVal = [];

    leadData.sort((a,b) => a.field_id - b.field_id)

    leadData.forEach((val) => {

        let value = '';

        if (identifierType === 'Form Group') {
            if (val.group_id === identifier) {
                value = decrypt(val.value);
                identifierVal.push({value: value, verify_type: val.verify_type});
            }
        } else if (identifierType === 'Group Field') {
            if (val.field_id === identifier) {
                value = decrypt(val.value);
                identifierVal.push({value: value, verify_type: val.verify_type});
            }
        }

    });

    return identifierVal;
}

/**
 * Retrieves and decrypts the value of a specified field from a lead's basic information.
 *
 * @param {Object} lead - The lead object containing basic information.
 * @param {string} identifier - The identifier of the field to retrieve.
 * @returns {string} - The decrypted value of the specified field, with extra spaces removed.
 */
export function getLeadFieldValue(lead, identifier)  {
    let identifierVal = [];
    let data = lead.basic_info;
    let field = data.filter(function (sf) {
        return sf.field_id === identifier
    });
    field.forEach(f => {
        identifierVal.push(decrypt(f.value));
    });
    return identifierVal.join(" ").replace(/\s\s+/g, ' ');
}

/**
 * Retrieves the custom value(s) associated with a given identifier from a lead object.
 *
 * @param {Object} lead - The lead object containing custom information.
 * @param {string} identifier - The label used to identify the custom value(s).
 * @returns {string} - A string of concatenated custom values associated with the identifier, with extra spaces removed.
 */
export function getLeadCustomValue(lead, identifier)  {
    let identifierVal = [];
    let data = lead.custom_info;
    let field = data.filter(function (sf) {
        return sf.label === identifier
    });
    field.forEach(f => {
        identifierVal.push(f.value);
    });
    return identifierVal.join(" ").replace(/\s\s+/g, ' ');
}

/**
 * Retrieves campaign data from a lead object.
 *
 * @param {Object} lead - The lead object containing campaign data.
 * @param {string|null} lead.ga_client_id - The Google Analytics client ID.
 * @param {Object|null} lead.campaign_data - The campaign data object.
 * @param {string|null} lead.campaign_data.source - The source of the campaign.
 * @param {string|null} lead.campaign_data.medium - The medium of the campaign.
 * @param {string|null} lead.campaign_data.campaign - The name of the campaign.
 * @param {string|null} lead.campaign_data.qualification - The qualification of the campaign.
 * @param {string|null} lead.campaign_data.channel - The channel of the campaign.
 * @returns {Object} An object containing the campaign data.
 * @returns {string} return.source - The source of the campaign.
 * @returns {string} return.medium - The medium of the campaign.
 * @returns {string} return.campaign - The name of the campaign.
 * @returns {string} return.qualification - The qualification of the campaign.
 * @returns {string} return.channel - The channel of the campaign.
 */
export function getCampaignData(lead){

    let data = {
        source: "",
        medium: "",
        campaign: "",
        qualification: "",
        channel: "",
    }

    let def = 'Awaiting data population';
    if(lead.ga_client_id === null) {
        def = 'Google Client ID Not Recorded';
    }

    data.source = def;
    data.medium = def;
    data.campaign = def;
    data.qualification = def;
    data.channel = def;

    if (lead.campaign_data != null) {

        let cd = lead.campaign_data;

        if (cd.source != null) {
            data.source = cd.source;
        }

        if (cd.medium != null) {
            data.medium = cd.medium;
        }

        if (cd.campaign != null) {
            data.campaign = cd.campaign;
        }

        if (cd.qualification != null) {
            data.qualification = cd.qualification;
        }

        if (cd.channel != null) {
            data.channel = cd.channel;
        }
    }

    return data;
}

/**
 * Extracts source data from a lead object.
 *
 * @param {Object} lead - The lead object containing campaign data.
 * @param {Object} [lead.campaign_data] - The campaign data associated with the lead.
 * @param {string} [lead.campaign_data.source] - The source of the campaign.
 * @param {string} [lead.campaign_data.medium] - The medium of the campaign.
 * @param {string} [lead.campaign_data.campaign] - The campaign name.
 * @param {string} [lead.campaign_data.channel] - The channel of the campaign.
 * @param {string} [lead.campaign_data.ga_client_id] - The Google Analytics client ID.
 * @param {string} [lead.origin] - The origin of the lead.
 * @returns {Object} An object containing the extracted source data.
 * @returns {string} return.source - The source of the campaign.
 * @returns {string} return.medium - The medium of the campaign.
 * @returns {string} return.campaign - The campaign name.
 * @returns {string} return.channel - The channel of the campaign.
 * @returns {string} return.ga_client_id - The Google Analytics client ID.
 * @returns {string} return.origin - The origin of the lead.
 */
export function getSourceData(lead){

    let data = {
        source: "",
        medium: "",
        campaign: "",
        // qualification: "",
        channel: "",
        ga_client_id: "",
        origin: ""
    }

    if (lead) {
        data.origin = lead.origin;
    }

    if (lead.campaign_data != null) {

        let cd = lead.campaign_data;

        if (cd.source != null) {
            data.source = cd.source;
        }

        if (cd.medium != null) {
            data.medium = cd.medium;
        }

        if (cd.campaign != null) {
            data.campaign = cd.campaign;
        }

        // if (cd.qualification != null) {
        //     data.qualification = cd.qualification;
        // }

        if (cd.channel != null) {
            data.channel = cd.channel;
        }


        if (cd.ga_client_id != null ){
            data.ga_client_id = cd.ga_client_id;
        }
    }

    return data;
}

/**
 * Compares two arrays for equality.
 * 
 * This function checks if two arrays have the same length and contain the same elements.
 * 
 * @param {Array} array1 - The first array to compare.
 * @param {Array} array2 - The second array to compare.
 * @returns {boolean} - Returns true if the arrays are equal, otherwise false.
 */
export function areArrayEqual(array1, array2) {
    if (array1.length === array2.length) {
        return array1.every(element => {
            if (array2.includes(element)) {
                return true;
            }

            return false;
        });
    }

    return false;
}

/**
 * Abbreviates a number using SI symbols (e.g., 1.2k, 3M, 4.5B).
 *
 * @param {number} number - The number to abbreviate.
 * @returns {string|number} - The abbreviated number with the appropriate SI symbol, or the original number if no abbreviation is needed.
 */
export function abbreviateNumber(number){

    const SI_SYMBOL = ["", "k", "M", "B", "T", "P", "E"];

    // what tier? (determines SI symbol)
    let tier = Math.log10(Math.abs(number)) / 3 | 0;

    // if zero, we don't need a suffix
    if(tier === 0) return number;

    // get suffix and determine scale
    let suffix = SI_SYMBOL[tier];
    let scale = Math.pow(10, tier * 3);

    // scale the number
    let scaled = number / scale;

    // format number and add suffix

    let result = scaled % 1 === 0 ? scaled : scaled.toFixed(1)

    return result + suffix;
}


/**
 * Returns the class icon name for a given influencer type.
 *
 * @param {string} type - The type of the influencer platform.
 * @returns {string} The class icon name corresponding to the influencer type.
 */
export function getInfluencerTypeClassIcon(type){
    switch (type) {
        case 'platform-facebook':
            return 'facebook';

        case 'platform-instagram':
            return 'instagram';

        case 'platform-twitter':
            return 'twitter';

        case 'platform-youtube':
            return 'youtube';

        case 'platform-tiktok':
            return 'tiktok';

        case 'platform-kumu':
            return 'kumu';

        default:
            return 'others';

    }
}

/**
 * Returns the CSS class name for the given influencer platform type.
 *
 * @param {string} type - The type of the influencer platform.
 * @returns {string} - The CSS class name corresponding to the influencer platform type.
 *
 * @example
 * getInfluencerTypeClassColor('platform-facebook'); // returns 'color-icon1'
 * getInfluencerTypeClassColor('platform-instagram'); // returns 'color-icon2'
 * getInfluencerTypeClassColor('platform-twitter'); // returns 'color-icon3'
 * getInfluencerTypeClassColor('platform-youtube'); // returns 'color-icon4'
 * getInfluencerTypeClassColor('platform-tiktok'); // returns 'color-icon5'
 * getInfluencerTypeClassColor('platform-kumu'); // returns 'color-icon6'
 * getInfluencerTypeClassColor('unknown-platform'); // returns 'color-icon0'
 */
export function getInfluencerTypeClassColor(type){
    switch (type) {
        case 'platform-facebook':
            return 'color-icon1';

        case 'platform-instagram':
            return 'color-icon2';

        case 'platform-twitter':
            return 'color-icon3';

        case 'platform-youtube':
            return 'color-icon4';

        case 'platform-tiktok':
            return 'color-icon5';

        case 'platform-kumu':
            return 'color-icon6';

        default:
            return 'color-icon0';

    }
}

/**
 * Returns the label for the given influencer type.
 *
 * @param {string} type - The type of the influencer platform.
 * @returns {string} The label corresponding to the influencer type.
 */
export function getInfluencerTypeLabel(type){
    switch (type) {
        case 'platform-facebook':
            return 'Likes';

        case 'platform-instagram':
            return 'Followers';

        case 'platform-twitter':
            return 'Followers';

        case 'platform-youtube':
            return 'Subscribers';

        case 'platform-tiktok':
            return 'Followers';

        case 'platform-kumu':
            return 'Followers';

        default:
            return 'Followers';

    }
}


/**
 * Monitors user activity and logs out the user after a period of inactivity.
 * 
 * This function sets up an interval to check for user inactivity and logs out the user
 * if the inactivity period exceeds the specified maximum inactivity time.
 * 
 * The function listens for various user activity events (e.g., mouse movements, key presses)
 * and resets the inactivity timer whenever any of these events are detected.
 * 
 * @function idleLogout
 * @example
 * idleLogout();
 */
export function idleLogout() {

    let secondsSinceLastActivity = 0;

    const maxInactivity = (/*30 * 60*/ 60 * 5);

    setInterval(function() {
        secondsSinceLastActivity++;
        if(secondsSinceLastActivity > maxInactivity) {
            localStorage.clear();
            window.location.href = '/login';
        }
    }, 1000);

    function activity() {
        secondsSinceLastActivity = 0;
    }

    const activityEvents = [
        'mousedown', 'mousemove', 'keydown',
        'scroll', 'touchstart', 'keypress'
    ];

    activityEvents.forEach(function (eventName) {
        document.addEventListener(eventName, activity, true);
    })

}


/**
 * Retrieves the session user from localStorage.
 *
 * @returns {Object} The data of the parsed user object from localStorage.
 */
export function getSessionUser(){
    let user = localStorage.getItem('user');
    let parsed = JSON.parse(user);
    return parsed.data;
}

/**
 * Validates if a given string is a valid URL.
 *
 * @param {string} urlString - The URL string to validate.
 * @returns {boolean} - Returns true if the URL is valid, otherwise false.
 */
export const isValidUrl = (urlString) => {
    var urlPattern = new RegExp('^(https?:\\/\\/)?'+ // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))'+ // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // validate port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?'+ // validate query string
        '(\\#[-a-z\\d_]*)?$','i'); // validate fragment locator
    return !!urlPattern.test(urlString);
}